import { BoxProps, colors, Typography, useTheme } from '@mui/material'
import { FlexRow, TokenAmount } from '@xylabs/sdk-react'
import { AuctionStatus, Quadkey } from '@xyo-network/sdk-xyoworld-js'

import ComponentSize from '../../../ComponentSize'
import { useAuction } from '../../../Contexts'
import QuadkeyMap from '../../QuadkeyMap'
import { useNavigateToQuadkey } from '../../useNavigateToQuadkey'

interface Props extends BoxProps {
  hideQuadkey?: boolean
  hideValue?: boolean
  onClick?: (event: React.MouseEvent) => void
  page?: string
  quadkey?: Quadkey
  size?: ComponentSize
  to?: string
}

const sizeToPixels: Record<ComponentSize, number> = {
  large: 512,
  medium: 256,
  small: 128,
  xlarge: 1024,
  xsmall: 64,
}

const AuctionCard: React.FC<Props> = (props) => {
  const { hideValue, hideQuadkey, quadkey, onClick, to, page = 'auction', size = 'medium', ...boxProps } = props
  const { auction } = useAuction(quadkey)
  const { navigateToQuadkey } = useNavigateToQuadkey()
  const theme = useTheme()

  const pixelSize = sizeToPixels[size]

  const price = auction?.price()

  const getColor = (): string => {
    switch (auction?.status()) {
      case AuctionStatus.Available:
        return colors.green[500]
      case AuctionStatus.Completed:
        return colors.blue[500]
      case AuctionStatus.Dutch:
        return colors.amber[500]
      case AuctionStatus.Live:
        return colors.green[500]
      case AuctionStatus.Minted:
        return theme.palette.common.black
      case AuctionStatus.Upcoming:
        return colors.grey[500]
      default:
        return colors.red[500]
    }
  }

  const onClickLocal = (event: React.MouseEvent) => {
    event.stopPropagation()
    if (onClick) {
      onClick(event)
    } else {
      navigateToQuadkey(quadkey ?? Quadkey.Zero, event, page, to)
    }
  }

  return (
    <FlexRow
      height={pixelSize}
      onClick={onClickLocal}
      style={{ borderRadius: theme.shape.borderRadius, cursor: 'pointer', overflow: 'hidden' }}
      width={pixelSize}
      position="relative"
      busy={!auction}
      {...boxProps}
    >
      <QuadkeyMap crop={true} quadkey={quadkey} size={pixelSize} />
      {!hideValue ? (
        <TokenAmount
          position="absolute"
          style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0, top: -2 }}
          textColor={theme.palette.getContrastText(getColor())}
          statusColor={getColor()}
          size="small"
          amount={price ?? null}
        />
      ) : null}
      {!hideQuadkey ? (
        <FlexRow
          bgcolor={theme.palette.common.black}
          color={theme.palette.getContrastText(theme.palette.common.black)}
          bottom={0}
          paddingTop={0.5}
          paddingX={1}
          position="absolute"
          style={{ borderTopLeftRadius: 5, borderTopRightRadius: 5, opacity: 0.5 }}
        >
          <Typography variant="body2">{`#${quadkey?.toBase4HashLabel()}`}</Typography>
        </FlexRow>
      ) : null}
    </FlexRow>
  )
}

export default AuctionCard
